import Text from "common/Typography/Text.tsx";
import Flex from "common/Boxes/Flex.tsx";
import UserAvatar from "common/Avatars/UserAvatar.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import Button from "common/Buttons/Button.tsx";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useDispatch, useSelector } from "react-redux";
import styled, { useTheme } from "styled-components";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import {
  BASE_REGISTER_PATH,
  ONBOARDING_SELECT_EXCHANGE_PATH,
} from "common/constants.ts";
import ListDescription from "features/configuration/InviteExternalUsers/ListDescription.tsx";
import React, { useContext } from "react";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import Tag from "common/Tags/Tag.tsx";
import { getExternalInvitePermissionText } from "features/configuration/common/utils.ts";
import {
  LanguageNameSpaces,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";

const ExternalInviteCreateAccountStep = () => {
  const theme = useTheme();
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const { historyPush } = useHistoryWithQuery();
  const {
    isUserLinkedToAnotherManager,
    isUserNotSubUser,
    isUserWithDifferencePermission,
    inviteData,
    handleUserConfirmed,
  } = useContext(ExternalConnectAccountContext)!;
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const isUseDifferentUserError =
    isUserLinkedToAnotherManager ||
    isUserNotSubUser ||
    isUserWithDifferencePermission;
  const dispatch = useDispatch();

  const getCurrentPermissionKey = (
    permission?: UserImpersonationPermissionLevel
  ): TranslationKeys<LanguageNameSpaces.AUTH> => {
    switch (permission) {
      case UserImpersonationPermissionLevel.READ:
        return "external_connect.confirm_user.current_permission_read_only";
      case UserImpersonationPermissionLevel.WRITE:
        return "external_connect.confirm_user.current_permission_read_write";
      case UserImpersonationPermissionLevel.NONE:
      default:
        return "external_connect.confirm_user.current_permission_no_access";
    }
  };

  const getDescriptions = () => {
    if (isUserLinkedToAnotherManager) {
      return [
        t("external_connect.confirm_user.linked_to_another_manager"),
        t("external_connect.confirm_user.use_unlinked_account"),
      ];
    } else if (isUserNotSubUser) {
      return [
        t("external_connect.confirm_user.not_sub_user"),
        t("external_connect.confirm_user.use_linked_sub_account"),
      ];
    } else if (isUserWithDifferencePermission) {
      return [
        t(getCurrentPermissionKey(user?.sub_user_permission_level)),
        t("external_connect.confirm_user.permission_mismatch_description"),
        t("external_connect.confirm_user.contact_admin"),
      ];
    }

    return [
      t("external_connect.confirm_user.continue_description", {
        permissionText: t(
          getExternalInvitePermissionText(
            inviteData
          ) as TranslationKeys<LanguageNameSpaces.AUTH>
        ),
      }),
      t("external_connect.confirm_user.switch_account_description"),
    ];
  };

  const clearTokens = () => {
    dispatch(sharedLib.slices.authentication.actions.clear());
  };

  const getTag = () => {
    if (!isUseDifferentUserError) return;
    let text = t("external_connect.confirm_user.account_already_linked");
    if (isUserNotSubUser)
      text = t("external_connect.confirm_user.sub_account_required");
    else if (isUserWithDifferencePermission)
      text = t("external_connect.confirm_user.permission_mismatch");
    return <StyledTag color={theme.colors.red}>{text}</StyledTag>;
  };

  return (
    <CreateAccountWrapper>
      <Text style={{ textAlign: "center", marginBottom: 16 }}>
        {t("external_connect.confirm_user.logged_in_as")}
      </Text>
      {user && (
        <Flex $justifyContent="center" style={{ marginBottom: 24 }}>
          <UserAvatar user={user} avatarSize={40} fallbackInitialsSize={2} />
          <UserInfoWrapper>
            <Text $weight={typography.fontWeights.MEDIUM}>
              {user?.full_name}
            </Text>
            <Text type="secondary">{user?.email}</Text>
          </UserInfoWrapper>
        </Flex>
      )}
      {getTag()}
      <ListDescription texts={getDescriptions()} />
      <CreateAccountButtons>
        <Button
          type="primary"
          style={{ width: "100%" }}
          onClick={() => {
            if (isUseDifferentUserError) {
              clearTokens();
              historyPush(BASE_REGISTER_PATH);
            } else {
              handleUserConfirmed(user?.id);
              historyPush(ONBOARDING_SELECT_EXCHANGE_PATH);
            }
          }}
        >
          {isUseDifferentUserError
            ? t("external_connect.confirm_user.use_different_account")
            : t("external_connect.confirm_user.continue")}
        </Button>
        {!isUseDifferentUserError && (
          <Button
            type="link"
            onClick={() => {
              clearTokens();
              historyPush(BASE_REGISTER_PATH);
            }}
          >
            {t("external_connect.confirm_user.use_different_account")}
          </Button>
        )}
      </CreateAccountButtons>
    </CreateAccountWrapper>
  );
};

export default ExternalInviteCreateAccountStep;

const StyledTag = styled(Tag)`
  width: max-content;
  margin: 0 auto 8px auto;
  font-size: 14px;
`;
const CreateAccountWrapper = styled.div`
  width: 100%;
  max-width: 530px;
  justify-content: center;
`;
const UserInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
`;
const CreateAccountButtons = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  max-width: 300px;
  align-items: center;
  margin: 24px auto 0 auto;
`;
